import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import MenuOpenIcon from "@material-ui/icons/MenuOpen";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import SidebarUserMenu from "../components/SidebarUserMenu";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import logoWhite from "../assets/kanal-logo-white.png";
import logoDark from "../assets/kanal-logo.png";
import markWhite from "../assets/kanal-mark-white.png";

const drawerWidth = 272;
const drawerCollapsedWidth = 84;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "100dvh",
    },
  },
  toolbar: {
    minHeight: theme.sizing.appBar,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(3),
    gap: theme.spacing(0.5),
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.appBar.background,
    color: theme.palette.appBar.text,
    boxShadow: "none",
    borderBottom: theme.border.divider,
    left: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["left", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
  appBarCollapsed: {
    left: drawerCollapsedWidth,
    width: `calc(100% - ${drawerCollapsedWidth}px)`,
  },
  appBarMobile: {
    left: 0,
    width: "100%",
  },
  menuButton: {
    color: theme.palette.text.secondary,
  },
  mobileLogo: {
    height: 28,
    marginLeft: theme.spacing(1),
  },
  grow: {
    flexGrow: 1,
  },
  headerIcon: {
    color: theme.palette.text.secondary,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    backgroundColor: theme.palette.sidebar.background,
    color: theme.palette.sidebar.text,
    border: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: drawerCollapsedWidth,
  },
  drawerDocked: {
    borderRight: 0,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: theme.sizing.appBar + 12,
    padding: theme.spacing(0, 3),
    flexShrink: 0,
  },
  brandCollapsed: {
    justifyContent: "center",
    padding: 0,
  },
  brandLogo: {
    height: 38,
    maxWidth: "100%",
    objectFit: "contain",
  },
  brandMark: {
    height: 34,
  },
  sidebarBody: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0, 1.5, 1.5),
  },
  navList: {
    flex: 1,
    overflowX: "hidden",
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255,255,255,0.18)",
      borderRadius: "6px",
    },
  },
  sidebarFooter: {
    borderTop: `1px solid ${theme.palette.sidebar.divider}`,
    paddingTop: theme.spacing(1.5),
  },
  appBarSpacer: {
    minHeight: theme.sizing.appBar,
  },
  content: {
    flex: 1,
    overflow: "auto",
    backgroundColor: theme.palette.background.default,
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const { loading } = useContext(AuthContext);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const drawerClose = () => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && !isMobile && classes.drawerPaperClose
          ),
          paperAnchorDockedLeft: classes.drawerDocked,
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <div
          className={clsx(
            classes.brand,
            !drawerOpen && !isMobile && classes.brandCollapsed
          )}
        >
          {drawerOpen || isMobile ? (
            <img src={logoWhite} alt="Kanal Publicidade" className={classes.brandLogo} />
          ) : (
            <img src={markWhite} alt="Kanal" className={classes.brandMark} />
          )}
        </div>
        <div className={classes.sidebarBody}>
          <List className={classes.navList}>
            <MainListItems drawerClose={drawerClose} collapsed={!drawerOpen && !isMobile} />
          </List>
          <div className={classes.sidebarFooter}>
            <SidebarUserMenu collapsed={!drawerOpen && !isMobile} />
          </div>
        </div>
      </Drawer>
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          !drawerOpen && !isMobile && classes.appBarCollapsed,
          isMobile && classes.appBarMobile
        )}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={classes.menuButton}
          >
            {drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
          {isMobile && (
            <img src={logoDark} alt="Kanal Publicidade" className={classes.mobileLogo} />
          )}
          <div className={classes.grow} />

          <IconButton
            aria-label={i18n.t("mainDrawer.appBar.darkMode")}
            onClick={toggleTheme}
            className={classes.headerIcon}
          >
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          {user.id && <NotificationsPopOver />}
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
